import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Models } from '@ibexfusion/models';
import { HttpClient } from '@angular/common/http';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { MemoryService } from '@services/memory/memory.service';
import { DataTableComponent, DataTableEvent } from '../../../components/data-table/data-table.component';
import { CompositionService } from '@services/compositions/composition.service';
import { CustomerService } from '@services/customers/customer.service';
import { OrganisationCustomerService } from '@services/customers/organisation-customer.service';
import { OrganisationService } from '@services/organisations/organisation.service';
import { PackageService } from '@services/packages/package.service';
import { Observable } from 'rxjs';
import { CustomerSelectionComponent } from '../../../components/popUpSelects/customer-selection/customer-selection.component';
import { OrganisationSelectionComponent } from '../../../components/popUpSelects/organisation-selection/organisation-selection.component';
import { AngieScreensService } from '@services/angie-screens/angie-screens.service';
import { PackageEraserComponent } from './package-eraser/package-eraser.component';

import * as _ from 'lodash';
import { MessengerService } from '@services/messenger/messenger.service';

@Component({
    selector: 'app-package-editor',
    templateUrl: './package-editor.component.html',
    styleUrls: ['./package-editor.component.scss'],
    providers: [PackageService, CompositionService, CustomerService, OrganisationCustomerService, OrganisationService, AngieScreensService],
    standalone: false
})
export class PackageEditorComponent implements OnInit, OnDestroy {

  @ViewChild('assignedItemsTable') assignedItemsTable: DataTableComponent;
  @ViewChild('assignedPackagesTable') assignedPackagesTable: DataTableComponent;
  @Input() onSave: Observable<any>;

  public title: string;
  public subtitle: string;

  public packageId;
  public myPackage: Models.Package;
  public baseUrlSegement = 'packages';
  public destinationPackages: Models.Package[];
  public assignedItemsProvider: MemoryService<Models.Composition>;
  public assignedPackagesProvider: MemoryService<Models.Package>;
  public customer;
  public ownerOrganisation;
  public columns = [{ prop: 'title' }];
  public hotelUrl = 'https://hotels.ibexfusion.com/info?lang=de&zip=';
  public hotelData = [];
  public columnsScreen = [{ prop: 'name' }, { prop: 'uuid' }];
  public isLoading = false;
  public superadmin = false;
  private subscription: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private snackbar: SnackbarService,
    public compositionService: CompositionService,
    public angieScreensService: AngieScreensService,
    private customerService: CustomerService,
    private organisationCustomerService: OrganisationCustomerService,
    private organisationService: OrganisationService,
    private http: HttpClient,
    public packageService: PackageService,
    private dialog: MatDialog,
    private ms: MessengerService
  ) {
  }

  async ngOnInit() {
    const privileges = this.ms.$superadminPrivileges.getValue();

    if (!!privileges) {
      for (const privilege of privileges) {
        if (privilege.privilegeName === 'superadmin' && privilege.value === 'packages') {
          this.superadmin = true;
        }
      }
    }

    this.isLoading = true;
    this.packageId = this.route.snapshot.params['id'];
    this.baseUrlSegement = this.router.url.split('/')[1];
    await this.fetchDestinationPackages();
    this.fetchCustomer();

    if (!this.packageId) {
      this.startFromScratch();
      this.isLoading = false;
      return;
    }

    await this.fetchPackage(this.packageId)
    this.fetchCustomer();
    this.fetchOrganisation();
    this.isLoading = false;

    this.subscription = this.onSave.subscribe(type => {
      if (type === 'meta-config') {
        this.savePackage();
      }
      if (type === 'meta-config-reload') {
        this.reloadPackage();
      }
    });
    this.reloadPackage();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  public onDetailScreen(event: DataTableEvent): void {
    this.router.navigate(['angie-screens', event.row._id]);
  }

  public onAssignScreen(event: DataTableEvent): void {
    const index = this.myPackage.screenUuids.indexOf(event.row.uuid);
    if (index >= 0) {
      this.myPackage.screenUuids.splice(index, 1);
    } else {
      this.myPackage.screenUuids.push(event.row.uuid);
    }
  }

  public async assignItem(event: DataTableEvent): Promise<any> {
    const alreadyAssigned = _.find(this.assignedItemsProvider.db, item => {
      return item._id === event.row._id
    });
    if (alreadyAssigned) {
      console.log('composition is already assigned!');
      return;
    }

    await this.assignedItemsProvider.create(event.row);
    this.assignedItemsTable.refresh();
  }

  public async removeAssignedItem(event: DataTableEvent): Promise<any> {
    await await this.assignedItemsProvider.remove(event.index);
    this.assignedItemsTable.refresh();
  }

  public async assignPackage(event: DataTableEvent): Promise<any> {
    const alreadyAssigned = _.find(this.assignedPackagesProvider.db, item => {
      return item._id === event.row._id
    });
    if (alreadyAssigned) {
      console.log('package is already assigned!');
      return;
    }

    await this.assignedPackagesProvider.create(event.row)
    this.assignedPackagesTable.refresh();
  }

  public async removeAssignedPackage(event: DataTableEvent): Promise<any> {
    await this.assignedPackagesProvider.remove(event.index);
    this.assignedPackagesTable.refresh();
  }

  public goToComposition(event: DataTableEvent): void {
    this.router.navigate(['compositions', event.row._id]);
  }

  public async savePackage(): Promise<any> {
    let compositionChanged = false;
    if (this.assignedItemsProvider.db.length !== this.myPackage.compositionIds.length ||
      (this.myPackage.assignedChildPackages && this.assignedPackagesProvider.db.length !== this.myPackage.assignedChildPackages.length)) {
      compositionChanged = true;
    }

    if (!this.packageId && !this.myPackage.title) {
      this.snackbar.open('Could not save, missing title! ');
      return;
    }

    if (this.assignedItemsProvider.db.length < this.myPackage.compositionIds.length) {
      // todo: check if apps are assigned form this composition
      console.log(1, 'todo: check if apps are assigned form this composition');
    }
    const compositionIds: string[] = [];
    for (let i = 0; i < this.assignedItemsProvider.db.length; i++) {
      compositionIds.push(this.assignedItemsProvider.db[i]._id);
    }
    this.myPackage.compositionIds = compositionIds;

    if (this.myPackage.assignedChildPackages && this.assignedPackagesProvider.db.length < this.myPackage.assignedChildPackages.length) {
      // todo: check if apps are assigned form this composition
      console.log(1, 'todo: check if apps are assigned form this composition');
    }
    const assignedChildPackageIds: string[] = [];
    for (let i = 0; i < this.assignedPackagesProvider.db.length; i++) {
      assignedChildPackageIds.push(this.assignedPackagesProvider.db[i]._id);
    }
    this.myPackage.assignedChildPackages = assignedChildPackageIds;

    let result;
    if (this.myPackage._id) {
      result = await this.packageService.update(this.myPackage._id, this.myPackage);
    } else {
      result = await this.packageService.create(this.myPackage);
    }

    await this.router.navigate([this.baseUrlSegement, result._id]);
    this.snackbar.success('Saved package');
  }

  addToMatchIds(value, key) {
    if (!value.length) {
      delete this.myPackage.tweebie.matchIds[key];
      return;
    }
    this.myPackage.tweebie.matchIds[key] = value;
  }

  public async assignCustomer() {
    let dialogRef: MatDialogRef<CustomerSelectionComponent>;
    const config: MatDialogConfig = <any>{ data: { ids: [this.myPackage.customerId] } };

    dialogRef = this.dialog.open(CustomerSelectionComponent, config);

    const result = await dialogRef.afterClosed().toPromise();
    if (result && result.customerId) {
      if (this.myPackage.customerId && result.customerId !== this.myPackage.customerId && this.myPackage.screenUuids && this.myPackage.screenUuids.length) {
        this.snackbar.open('Could not change customer. Please remove first all assigned screeens.');
        return;
      }
      this.myPackage.customerId = result.customerId;
      this.fetchCustomer();
    }
  }

  public removeAssignedCustomer() {
    this.myPackage.customerId = null;
    this.customer = null;
  }

  public async assignOwnerOrganisation() {
    let dialogRef: MatDialogRef<OrganisationSelectionComponent>;
    dialogRef = this.dialog.open(OrganisationSelectionComponent, {});

    const result = await dialogRef.afterClosed().toPromise()
    if (result && result.OrganisationID) {
      this.myPackage.OwnerOrganisationID = result.OrganisationID;
      this.ownerOrganisation = result.title;
    }
  }

  public removeOwnerOrganisation() {
    this.myPackage.OwnerOrganisationID = null;
    this.ownerOrganisation = null;
  }

  public deleteAllData() {
    const config: MatDialogConfig = <any>{ data: { package: this.myPackage, compositions: this.assignedItemsProvider.db } };
    this.dialog.open(PackageEraserComponent, config);
  }

  private async reloadPackage() {
    this.isLoading = true;
    const myPackage = await this.packageService.get(this.route.snapshot.params['id']);
    this.myPackage = myPackage;
    if (this.myPackage.isScreenPackage && !this.myPackage.ibexApp) {
      this.myPackage.ibexApp = new Models.PackageIbexApp();
    }
    if (this.myPackage.isScreenPackage && !this.myPackage.ibexTouch) {
      this.myPackage.ibexTouch = new Models.PackageIbexTouch();
      this.myPackage.ibexTouch.populateFromJSON(myPackage.ibexTouch);
    }
    this.isLoading = false;
    if (this.myPackage && this.myPackage.combinedAddressCached && this.myPackage.combinedAddressCached.zip) {
      this.hotelData = <any>await this.http.get(this.hotelUrl + this.myPackage.combinedAddressCached.zip).toPromise();
      if (this.hotelData) {
        this.hotelData.sort(function(a, b) {
          if (a.name < b.name) {
            return -1;
          }
          if (a.name > b.name) {
            return 1;
          }
          return 0;
        });
      }
    }
  }

  private startFromScratch(): void {
    this.assignedItemsProvider = new MemoryService(<Models.Composition[]>[]);
    this.assignedPackagesProvider = new MemoryService(<Models.Package[]>[]);
    this.title = 'Create new package';
    this.myPackage = new Models.Package();
    this.myPackage.populateFromJSON({
      title: null,
      linkName: '',
      compositionIds: [],
      tweebie: {
        isDestination: false,
        isComingSoon: false,
        isDevOnly: false,
        destinationId: null
      },
      ibexApp: new Models.PackageIbexApp(),
      ibexTouch: new Models.PackageIbexTouch()
    });
  }

  private async fetchPackage(packageId: string): Promise<void> {
    this.myPackage = await this.packageService.get(packageId);
    this.title = this.myPackage.title;
    if (!this.myPackage.tweebie) {
      this.myPackage.tweebie = new Models.PackageTweebie();
      this.myPackage.tweebie.populateFromJSON({
        isDestination: false,
        destinationId: null,
        selectionImage: []
      });
    }

    if (this.myPackage.assignedChildPackages && this.myPackage.assignedChildPackages.length > 0) {
      const response = await this.packageService.find(0, 100, { _id: { $in: this.myPackage.assignedChildPackages } });
      this.assignedPackagesProvider = new MemoryService(response.data);
    } else {
      this.assignedPackagesProvider = new MemoryService(<Models.Package[]>[]);
    }

    if (this.myPackage.compositionIds && this.myPackage.compositionIds.length > 0) {
      const response = await this.compositionService.find(0, 100, { _id: { $in: this.myPackage.compositionIds } });
      this.assignedItemsProvider = new MemoryService(response.data);
    } else {
      this.assignedItemsProvider = new MemoryService(<Models.Composition[]>[]);
    }
  }

  private async fetchCustomer() {
    if (this.myPackage && this.myPackage.customerId) {
      const organisationCustomer = await this.organisationCustomerService.find(0, 1, { _id: this.myPackage.customerId });
      if (organisationCustomer.data && organisationCustomer.data.length) {
        this.customer = organisationCustomer.data[0].organisationName;
        return;
      }
      const customer = await this.customerService.find(0, 1, { _id: this.myPackage.customerId });
      if (customer.data && customer.data.length) {
        this.customer = customer.data[0].company;
      }
    }
  };

  private async fetchOrganisation() {
    if (this.myPackage && this.myPackage.OwnerOrganisationID) {
      const organisation = await this.organisationService.get(this.myPackage.OwnerOrganisationID);
      if (organisation) {
        this.ownerOrganisation = organisation.title;
      }
    }
  };

  private async fetchDestinationPackages(): Promise<void> {
    const response = await this.packageService.find(0, 100, { 'tweebie.isDestination': true });
    this.destinationPackages = response.data;
  }
}
